import { MenuItem, Divider } from '@mui/material';
import { useCustomSnackbar } from 'components/NotificationSnackbars';
import {
  Workspace,
  getWorkspacePreferenceKey,
  getSystemWorkspacePreferenceKey,
  useWorkspacesMenu,
  useSystemWorkspacesMenu,
} from 'contexts/WorkspaceContext';
import { PrivilegeType, KineticModule } from '@kinetic-ui/data-layer';
import {
  useUserTenantPreference,
  useOrganizationWidePreference,
  useAnyPrivilege,
} from 'kineticUtils/hooks';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import shortid from 'shortid';
import { NewWorkspaceTitle } from './NewWorkspaceTitle';
import { WorkspacesHeader } from './WorkspacesHeader';
import { getWorkspaceItem } from './utils';

const AddToUserWorkspace = ({
  workspaceId,
  pageTitle,
}: {
  workspaceId: string;
  pageTitle: string;
}) => {
  const { t } = useTranslation('workspace');
  const { value, loading, setPreference } = useUserTenantPreference<Workspace>({
    key: getWorkspacePreferenceKey(workspaceId),
  });
  const { enqueueCustomSnackbar } = useCustomSnackbar();

  const location = useLocation();

  const onSave = () => {
    const id = shortid();
    setPreference({
      ...value,
      layout: {
        ...value.layout,
        [id]: getWorkspaceItem({ id, title: pageTitle, to: location.pathname }),
      },
    })
      .then(() => {
        enqueueCustomSnackbar.success(
          t('addPageToWorkspaceSuccess', { pageTitle, workspaceTitle: value.title })
        );
      })
      .catch(() => {
        enqueueCustomSnackbar.error(
          t('addPageToWorkspaceError', { pageTitle, workspaceTitle: value.title })
        );
      });
  };

  return loading ? null : <MenuItem onClick={onSave}>{value.title}</MenuItem>;
};

const AddToSystemWorkspace = ({
  workspaceId,
  pageTitle,
}: {
  workspaceId: string;
  pageTitle: string;
}) => {
  const { t } = useTranslation('workspace');
  const { value, loading, setPreference } = useOrganizationWidePreference<Workspace>({
    key: getSystemWorkspacePreferenceKey(workspaceId),
  });
  const location = useLocation();
  const { enqueueCustomSnackbar } = useCustomSnackbar();

  const onSave = () => {
    const id = shortid();
    setPreference({
      ...value,
      layout: {
        ...value.layout,
        [id]: getWorkspaceItem({ id, title: pageTitle, to: location.pathname }),
      },
    })
      .then(() => {
        enqueueCustomSnackbar.success(
          t('addPageToWorkspaceSuccess', { pageTitle, workspaceTitle: value.title })
        );
      })
      .catch(() => {
        enqueueCustomSnackbar.error(
          t('addPageToWorkspaceError', { pageTitle, workspaceTitle: value.title })
        );
      });
  };

  return loading ? null : <MenuItem onClick={onSave}>{value.title}</MenuItem>;
};

const UserWorkspaces = ({
  pageTitle,
  showNew,
  onCancel,
  onNewClick,
}: {
  pageTitle: string;
  showNew: boolean;
  onCancel: () => void;
  onNewClick: () => void;
}) => {
  const { t } = useTranslation('workspace');
  const { userWorkspaces, addWorkspace } = useWorkspacesMenu();
  const location = useLocation();
  const { enqueueCustomSnackbar } = useCustomSnackbar();

  const onSave = (newWorkspaceTitle: string) => {
    const itemId = shortid();
    addWorkspace({
      title: newWorkspaceTitle,
      layout: {
        [itemId]: getWorkspaceItem({ id: itemId, title: pageTitle, to: location.pathname }),
      },
    })
      .then(() => {
        enqueueCustomSnackbar.success(
          t('addPageToWorkspaceSuccess', { pageTitle, workspaceTitle: newWorkspaceTitle })
        );
        onCancel();
      })
      .catch(() => {
        enqueueCustomSnackbar.error(
          t('addPageToWorkspaceError', { pageTitle, workspaceTitle: newWorkspaceTitle })
        );
      });
  };

  return (
    <>
      <WorkspacesHeader
        tooltip={t('addToNewPersonalWorkspace')}
        title={t('myWorkspaces')}
        onClick={onNewClick}
      />
      {showNew && <NewWorkspaceTitle onSave={onSave} onCancel={onCancel} />}
      {userWorkspaces.map((w) => (
        <AddToUserWorkspace key={w.id} workspaceId={w.id} pageTitle={pageTitle} />
      ))}
    </>
  );
};

const SystemWorkspaces = ({
  pageTitle,
  showNew,
  onCancel,
  onNewClick,
}: {
  pageTitle: string;
  showNew: boolean;
  onCancel: () => void;
  onNewClick: () => void;
}) => {
  const { t } = useTranslation('workspace');
  const { systemWorkspaces, addWorkspace } = useSystemWorkspacesMenu();
  const location = useLocation();
  const { enqueueCustomSnackbar } = useCustomSnackbar();

  const hasCreatePrivilege = useAnyPrivilege([PrivilegeType.Create], KineticModule.System);
  const hasUpdatePrivilege = useAnyPrivilege([PrivilegeType.Update], KineticModule.System);

  const onSave = (newWorkspaceTitle: string) => {
    const itemId = shortid();
    addWorkspace({
      title: newWorkspaceTitle,
      layout: {
        [itemId]: getWorkspaceItem({ id: itemId, title: pageTitle, to: location.pathname }),
      },
    })
      .then(() => {
        enqueueCustomSnackbar.success(
          t('addPageToWorkspaceSuccess', { pageTitle, workspaceTitle: newWorkspaceTitle })
        );
        onCancel();
      })
      .catch(() => {
        enqueueCustomSnackbar.error(
          t('addPageToWorkspaceError', { pageTitle, workspaceTitle: newWorkspaceTitle })
        );
      });
  };

  return (
    <>
      <WorkspacesHeader
        tooltip={hasCreatePrivilege ? t('addToNewSystemWorkspace') : ''}
        title={t('systemWorkspaces')}
        onClick={hasCreatePrivilege ? onNewClick : undefined}
        disabled={!hasCreatePrivilege}
      />
      {showNew && <NewWorkspaceTitle onSave={onSave} onCancel={onCancel} />}
      {hasUpdatePrivilege &&
        systemWorkspaces.map((w) => (
          <AddToSystemWorkspace key={w.id} workspaceId={w.id} pageTitle={pageTitle} />
        ))}
    </>
  );
};

export const PageWorkspacesMenu = ({ pageTitle }: { pageTitle: string }) => {
  const hasCreatePrivilege = useAnyPrivilege([PrivilegeType.Create], KineticModule.System);
  const hasUpdatePrivilege = useAnyPrivilege([PrivilegeType.Update], KineticModule.System);
  const [newType, setNewType] = useState<'user' | 'system'>();
  const onCancel = () => {
    setNewType(undefined);
  };

  return (
    <>
      <UserWorkspaces
        onNewClick={() => {
          setNewType('user');
        }}
        pageTitle={pageTitle}
        showNew={newType === 'user'}
        onCancel={onCancel}
      />
      {(hasCreatePrivilege || hasUpdatePrivilege) && (
        <>
          <Divider />
          <SystemWorkspaces
            onNewClick={() => {
              setNewType('system');
            }}
            pageTitle={pageTitle}
            showNew={newType === 'system'}
            onCancel={onCancel}
          />
        </>
      )}
    </>
  );
};
